// frontend/src/domains/viewer/ViewportsConfigurations/stepBuckets.ts
import { traceHPMutation } from 'domains/viewer/HangingProtocol/hpMutationTrace';
import type { DehydratedViewportsConfigurations } from './types';

/**
 * The bucket a viewport override belongs to when there is no clinical step to attribute it to: a
 * non-stepped hanging protocol, or the Stepped HP flag off. Deliberately a string that cannot collide
 * with a step smid.
 */
export const FLAT_BUCKET = '__flat__';

/** A step smid, or {@link FLAT_BUCKET}. */
export type StepBucketKey = string;

/**
 * Viewport overrides partitioned by the step they were made on.
 *
 * A `null` value is meaningfully different from a missing key only to the persisted-record readers;
 * every consumer here treats both as "no overrides for this step".
 */
export type BucketedViewports = Record<StepBucketKey, Nullable<DehydratedViewportsConfigurations>>;

/** The overrides recorded against `bucket`, or null when that step has none. */
export function readBucket(
  byStep: Nullable<BucketedViewports>,
  bucket: StepBucketKey
): Nullable<DehydratedViewportsConfigurations> {
  return byStep?.[bucket] ?? null;
}

/**
 * Unions an incoming broadcast's buckets over the local ones, incoming winning per key.
 *
 * A whole-record replacement is wrong here, and only became wrong once one record started holding
 * every step. Each window rebuilds `byStep` from its own snapshot and broadcasts the result, so a
 * window that has never seen step B publishes a record without it; replacing wholesale then drops B's
 * overrides in the receiver, and the user finds them gone on switching back. Per-key is the smallest
 * merge that fixes it: the sender is authoritative about buckets it names and says nothing about the
 * rest.
 *
 * A deliberate clear still crosses, because clearing writes the bucket key holding `{}` rather than
 * removing it -- the key is present, so it wins its slot.
 *
 * **Deletion deliberately does not cross, and does not need to.** `pruneStepBuckets` and
 * `clearAllStepBuckets` run in each window's own effects, keyed on the broadcast step list and the
 * active HP smid, so both windows reach the same deletions from the same inputs. Making absence
 * authoritative here would instead let a window that is merely behind delete buckets it has not
 * learned about yet -- the failure this function exists to prevent.
 *
 * **An incoming bucket may not reduce a bucket's resolution (VX-4810).** Per-key "incoming wins" has no
 * notion of recency, and these values are published twice for the same bucket: once from step data that
 * is still criteria-only (`study_smid`/`series_smid` null, because a stepped HP is stored that way) and
 * again once the per-case match has resolved them. Both crossings are echoed between windows, so the
 * unresolved one can arrive *after* the resolved one and overwrite it. The viewer then rebuilds the grid
 * from null smids and hangs nothing -- only in the window that published, since the peer already holds
 * the resolved value locally, which is exactly the reported viewer/0-goes-blank-while-viewer/1-is-fine.
 *
 * A resolved value strictly dominates an unresolved one for the same bucket: they describe the same
 * viewports, one of them just knows which series each resolves to. So a crossing that would lower the
 * count of resolved viewports is a stale echo and is dropped. This is the same rule
 * `activeHangingProtocolSmidState`'s merge already applies to transient null smids, one atom over.
 */

/** How many of a bucket's viewports know which series they hang. Absent/empty buckets count as 0. */
function resolvedViewportCount(configs: Nullable<DehydratedViewportsConfigurations>): number {
  if (configs == null) return 0;
  return Object.values(configs).filter((config) => config?.series?.smid != null).length;
}

export function mergeBucketsFromBroadcast(
  oldByStep: Nullable<BucketedViewports>,
  newByStep: Nullable<BucketedViewports>,
  {
    /**
     * Drop a crossing that would lower a bucket's resolved-viewport count.
     *
     * **Only correct for `dehydratedHangingProtocol`**, whose contents are derived match results, so a
     * less-resolved value for the same bucket is always the stale one. It is wrong for
     * `viewportsConfigurationsState`: there a bucket holds the *user's* overrides, and clearing a
     * viewport deliberately lowers that count -- `clearStepViewportOverrides` writes `{}` for the
     * bucket. Guarding that atom would stop a clear crossing windows, which the merge is explicitly
     * documented above to support. Hence opt-in rather than the default.
     */
    rejectLessResolved = false,
  }: { rejectLessResolved?: boolean } = {}
): BucketedViewports {
  // Optional chaining rather than a plain read: these values arrive off a BroadcastChannel, so the
  // declared type is a claim about our own writes, not a guarantee about what lands in the handler.
  const merged: BucketedViewports = { ...(oldByStep ?? {}) };
  for (const [bucket, incoming] of Object.entries(newByStep ?? {})) {
    if (
      rejectLessResolved &&
      resolvedViewportCount(incoming) < resolvedViewportCount(merged[bucket])
    ) {
      // Traced because this drop is otherwise invisible, and its invisibility is expensive. The
      // receiving window keeps rendering what it already had, with no write, no warning and nothing
      // in either window's state to say a correction was refused -- so the symptom shows up
      // arbitrarily far away as "viewer/1 is stale" and reads like a bug in whatever last touched
      // the viewport. VX-5148 cost several rounds of investigation for exactly this reason: a
      // deliberate clear published as `resolved=0` was dropped here, and the stale value it was
      // meant to remove had no traceable writer.
      // Thunked, per this module's tracing convention: this sits on the broadcast merge path, which
      // runs on every crossing, so a disabled trace must cost one boolean rather than four
      // `Object.keys` walks.
      traceHPMutation('mergeBucketsFromBroadcast.rejectedLessResolved', () => ({
        bucket,
        incomingViewports: Object.keys(incoming ?? {}).length,
        incomingResolved: resolvedViewportCount(incoming),
        heldViewports: Object.keys(merged[bucket] ?? {}).length,
        heldResolved: resolvedViewportCount(merged[bucket]),
      }));
      continue;
    }
    merged[bucket] = incoming;
  }
  return merged;
}

/**
 * Whether any bucket has been published for this record.
 *
 * Exists because `byStep` replaced a value that used to be nullable, and "is there a hanging protocol
 * here" was previously asked as a null check on the whole record. The bucketed record is never null --
 * its default is `{ byStep: {} }` -- so that question needs an explicit predicate or every caller
 * silently answers yes.
 */
export function hasAnyBucket(byStep: Nullable<BucketedViewports>): boolean {
  return Object.keys(byStep ?? {}).length > 0;
}

/** `byStep` with `bucket` replaced. Every other bucket is carried across by reference. */
export function writeBucket(
  byStep: Nullable<BucketedViewports>,
  bucket: StepBucketKey,
  value: Nullable<DehydratedViewportsConfigurations>
): BucketedViewports {
  return { ...(byStep ?? {}), [bucket]: value };
}

/**
 * Moves `from`'s contents to `to`.
 *
 * Exists for the optimistic-create path: a step is added under a temporary smid, the user immediately
 * drags a series into it, and the server then hands back the real smid. Without this the overrides
 * would stay stranded under the temp key -- the same orphaning slice 2 hit with `clinicalSnapshots`.
 */
export function renameBucket(
  byStep: BucketedViewports,
  from: StepBucketKey,
  to: StepBucketKey
): BucketedViewports {
  if (!(from in byStep)) return byStep;
  const { [from]: moved, ...rest } = byStep;
  return { ...rest, [to]: moved };
}

/**
 * Drops buckets for steps that no longer exist, so a long-lived case record cannot grow without
 * bound. {@link FLAT_BUCKET} always survives -- it is not a step and no step list can vouch for it.
 *
 * Returns the input by reference when nothing was dropped, so a prune on every step write does not
 * churn the atom.
 */
export function pruneBuckets(
  byStep: BucketedViewports,
  liveStepSmids: readonly string[]
): BucketedViewports {
  const keep = new Set([FLAT_BUCKET, ...liveStepSmids]);
  const surviving = Object.keys(byStep).filter((key) => keep.has(key));
  if (surviving.length === Object.keys(byStep).length) return byStep;
  return Object.fromEntries(surviving.map((key) => [key, byStep[key]]));
}
